import { Status } from "@/app/shared_enums/enums";
import { Department, KioskDepartmentServicesService, ServiceofDepartment } from "@/app/types/department";
import { Service } from "@/app/types/service";
import { apiSlice } from "@/features/api/ApiSlice";
export enum TicketStatus {
  SERVING = "serving",
  SERVED = "served",
  CANCELLED = "cancelled",
}
export interface TicketResponse {
  detail: string;
  data: string;
  time: string;
  title: string;
  description?: string;
  id: number;
  user_id: number;
  ticket_number: any;
  ticket_id: string;
  reserved_number: string;
  assigned_number: string;
  org_id: number;
  service_id: number;
  service_name: string;
  closing_time: string;
  counter_id: number;
  counter_code: string;
  status: TicketStatus; // Add other possible statuses if needed
  eta: number;
  feedback: string;
  created_at: string; // Alternatively, Date if parsing as Date object
  is_deleted: boolean;
}

interface TableResponse<T> {
  data: T[];
  total_count: number;
}

type KioskDepartments = TableResponse<Department>;

type SericeOfDepartment = TableResponse<ServiceofDepartment>;
export const Kiosk = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllkioskDepartment: builder.query<KioskDepartments, { query: string }>({
      query: ({ query }) => ({
        url: `/kiosk/departments${query.length > 0 ? `?${query}` : ""}`,
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
      providesTags: ["Department"],
    }),
    getAllDepartmentServiceById: builder.query<
      TableResponse<Service>,
      { department_id: number; query: string }
    >({
      query: ({ department_id, query }) => ({
        url: `/kiosk/services/${department_id}${query.length > 0 ? `?${query}` : ""}`,
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }),
      providesTags: ["Department"],
      transformResponse: (response: TableResponse<KioskDepartmentServicesService>, _meta, _arg): TableResponse<Service> => {
        return {
          total_count: response.total_count,
          data: response.data.map((item: any) => {
            const serviceMeta = item.metadata?.find((m: any) => m.key === "service")?.value;

            return {
              id: item.tag_id,
              path: item.path,
              name: serviceMeta?.name ?? item.name,
              department_id: item.org_id ?? "unknown", // default if missing
              average_processing_time: serviceMeta?.average_processing_time ?? 0,
              created_at: item.created_at,
              contact_person: serviceMeta?.lead_id ?? undefined,
              contact_number: serviceMeta?.contact_number ?? undefined,
              is_public: serviceMeta?.visibility === "visible" ? true : false,
              status: serviceMeta?.status ? Status.Active : Status.Inactive,
              logo: serviceMeta?.logo ?? undefined,
              tags: serviceMeta?.tags ?? [],
              schedules: serviceMeta?.schedules ?? [],
              is_multi_step: serviceMeta?.is_multi_step ?? false,
              steps: serviceMeta?.steps ?? [],
              assigned_counters: serviceMeta?.assigned_counters ?? [],
              code: item.code,
              form_id: serviceMeta.form_id,
              form_slug: item.form_slug,
              event_id: item.event_id,
            };
          }),
        }
      }
    }),

    generateTicketForService: builder.mutation<TicketResponse, {path: number[], event_id: number}>({
      query: ({path, event_id}) => {
        // Construct URL using URLSearchParams
        const now = new Date();

        // Convert to local date (YYYY-MM-DD)
        const localDate = new Date(
          now.getTime() - now.getTimezoneOffset() * 60000
        )
          .toISOString()
          .split("T")[0];

        // Format time correctly in 24-hour format (HH:mm:ss)
        const localTime = now.toLocaleTimeString("en-GB", {
          hourCycle: "h23",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });

        const params = new URLSearchParams({
          title: " ",
          date: localDate, // Uses local date, not UTC
          time: localTime, // Uses properly formatted local time
          eta: "0",
          tag_id: String(path[path.length - 1]),
          path: "/" + path.join("/"),
          event_id: String(event_id)
        });
        // Final dynamic URL
        const requestUrl = `/kiosk/ticket/generate?${params.toString()}`;
        return {
          url: requestUrl,
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          body: {
            manual_eta: "0",
          },
        };
      },
      invalidatesTags: ["Ticket", "WaitingList"],
    }),

    generateUserTicketForService: builder.mutation<
      TicketResponse,
      {
        service_id: number;
        title: string;
        date: string;
        time: string;
        description?: string;
        user_id: number;
        r_ticket_id?: number;
        eta: number;
      }
    >({
      query: (serviceData) => {
        // Construct URL using URLSearchParams
        const params = new URLSearchParams({
          user_id: serviceData.user_id.toString(),
          title: serviceData.title,
          date: serviceData.date,
          time: `${serviceData.time}`, // Ensuring time format HH:mm:ss
          eta: serviceData.eta.toString(),
        });

        // Append optional parameters if they exist
        if (serviceData.description) {
          params.append("description", serviceData.description);
        }
        if (serviceData.r_ticket_id) {
          params.append("r_ticket_id", serviceData.r_ticket_id.toString());
        }

        // Final dynamic URL
        const requestUrl = `/kiosk/ticket/generate/${serviceData.service_id}?${params.toString()}`;

        return {
          url: requestUrl,
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          body: {
            manual_eta: serviceData.eta,
          },
        };
      },
      invalidatesTags: ["Ticket", "WaitingList"],
    }),
    generateUserTicketFromService: builder.mutation<
      TicketResponse,
      {
        title: string;
        date: string;
        time: string;
        is_admin: boolean;
        description?: string;
        user_id?: number;
        eta: number;
        counter?: number;
        assigned_to?: string;
        status: string;
        tag_id?: number;
        path?: string;
        event_id?: number;
        r_ticket_id?: number;
      }
    >({
      query: (serviceData) => {
        // Construct URL using URLSearchParams
        const params = new URLSearchParams({
          tag_id: serviceData.tag_id?.toString() || '',
          path: serviceData.path || '',
          title: serviceData.title,
          date: serviceData.date,
          time: `${serviceData.time}`, // Ensuring time format HH:mm:ss
          ticket_status: serviceData.status,
          is_admin: serviceData.is_admin ? "true" : "false",
        });

        // Append optional parameters if they exist
        if (serviceData.description) {
          params.append("description", serviceData.description);
        }

        if (serviceData.user_id) {
          params.append("user_id", serviceData.user_id.toString());
        }

        if (serviceData.event_id !== undefined && serviceData.event_id !== null) {
          params.append("event_id", serviceData.event_id.toString());
        }

        if (serviceData.r_ticket_id) {
          params.append("r_ticket_id", serviceData.r_ticket_id.toString());
        }

        return {
          url: `/kiosk/ticket/generate?${params.toString()}`,
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          body: {
            manual_eta: serviceData.eta,
            ...(serviceData.counter !== undefined ? { counter: serviceData.counter } : {}),
            ...(serviceData.assigned_to ? { assigned_to: serviceData.assigned_to } : {})
          },
        };
      },
      invalidatesTags: ["Ticket", "WaitingList"],
    }),
  }),
});

export const {
  useGetAllkioskDepartmentQuery,
  useGetAllDepartmentServiceByIdQuery,
  useLazyGetAllDepartmentServiceByIdQuery,
  useGenerateTicketForServiceMutation,
  useGenerateUserTicketForServiceMutation,
  useGenerateUserTicketFromServiceMutation
} = Kiosk;
